import React, { useRef, useEffect, useState, useMemo } from "react";
import TextUnit from "./TextUnit";
import Molecule from "./Molecule";

type BBox = [number, number, number, number];

/** Returns the fraction of bboxA's area that is inside bboxB (0–1). */
const overlapFraction = (a: BBox, b: BBox): number => {
  const [ax1, ay1, ax2, ay2] = a;
  const [bx1, by1, bx2, by2] = b;

  const interX1 = Math.max(ax1, bx1);
  const interY1 = Math.max(ay1, by1);
  const interX2 = Math.min(ax2, bx2);
  const interY2 = Math.min(ay2, by2);

  if (interX2 <= interX1 || interY2 <= interY1) return 0;

  const interArea = (interX2 - interX1) * (interY2 - interY1);
  const aArea = (ax2 - ax1) * (ay2 - ay1);
  return aArea > 0 ? interArea / aArea : 0;
};

type ObjectData = {
  cls: "text_unit" | "molecule" | "table" | "text_block";
  bbox: BBox;
  metadata?: {
    text?: string;
    smiles?: string;
    block_kind?: string;
  };
  uuid?: string;
};

type ImageDisplayProps = {
  data: { objects: ObjectData[] } | null;
  image: HTMLImageElement | null;
  selectedTerm?: string;
  onHoverObject?: (uuid: string | null) => void;
  highlightedObjectUuid?: string | null | undefined;
  highlightedBbox?: number[] | null;
  bboxVisibility?: { text: boolean; molecules: boolean; tables: boolean } | undefined;
};

type DisplayObject = {
  cls: "text_unit" | "molecule" | "table" | "text_block";
  x: number;
  y: number;
  width: number;
  height: number;
  transcription?: string | undefined;
  uuid?: string | undefined;
  blockKind?: string | undefined;
};

const ImageDisplay: React.FC<ImageDisplayProps> = ({
  data,
  image,
  selectedTerm,
  onHoverObject,
  highlightedObjectUuid,
  highlightedBbox,
  bboxVisibility = { text: true, molecules: true, tables: true },
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [imageProps, setImageProps] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [objects, setObjects] = useState<DisplayObject[]>([]);

  // Build a set of text_unit UUIDs that are >=60% inside any molecule bbox
  const suppressedTextUnitUuids = useMemo(() => {
    const suppressed = new Set<string>();
    if (!data?.objects) return suppressed;
    const textUnits = data.objects.filter(o => o.cls === "text_unit");
    const molecules = data.objects.filter(o => o.cls === "molecule");
    for (const tu of textUnits) {
      if (!tu.uuid) continue;
      for (const mol of molecules) {
        if (overlapFraction(tu.bbox, mol.bbox) >= 0.6) {
          suppressed.add(tu.uuid);
          break;
        }
      }
    }
    return suppressed;
  }, [data]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0]!.contentRect;
      setStageSize({ width, height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (
      !image ||
      !image.naturalWidth ||
      !image.naturalHeight ||
      stageSize.width === 0 ||
      stageSize.height === 0
    ) {
      setImageProps({ x: 0, y: 0, width: 0, height: 0 });
      return;
    }

    const scale = Math.min(
      stageSize.width / image.naturalWidth,
      stageSize.height / image.naturalHeight
    );
    const newWidth = image.naturalWidth * scale;
    const newHeight = image.naturalHeight * scale;
    const newX = (stageSize.width - newWidth) / 2;
    const newY = (stageSize.height - newHeight) / 2;

    setImageProps({ x: newX, y: newY, width: newWidth, height: newHeight });
  }, [image, stageSize]);

  useEffect(() => {
    if (data?.objects && Array.isArray(data.objects) && imageProps.width > 0 && imageProps.height > 0) {
      setObjects(
        data.objects.map((object) => {
          const [x1, y1, x2, y2] = object.bbox;
          const transcription =
            object.metadata?.text || object.metadata?.smiles || undefined;

          return {
            cls: object.cls,
            x: x1 * imageProps.width + imageProps.x,
            y: y1 * imageProps.height + imageProps.y,
            width: (x2 - x1) * imageProps.width,
            height: (y2 - y1) * imageProps.height,
            transcription,
            uuid: object.uuid,
            blockKind: object.metadata?.block_kind,
          };
        })
      );
    } else {
      setObjects([]);
    }
  }, [data, imageProps]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex items-center justify-center overflow-hidden"
    >
      <div
        className="relative"
        style={{ width: stageSize.width, height: stageSize.height }}
      >
        {image && (
          <img
            src={image.src}
            alt="Document"
            className="absolute select-none pointer-events-none ring-2 ring-black"
            style={{
              left: imageProps.x,
              top: imageProps.y,
              width: imageProps.width,
              height: imageProps.height,
            }}
          />
        )}
        {/* Highlighted object overlay (from chemistry tab hover) */}
        {highlightedObjectUuid && objects.map((object, idx) => {
          if (object.uuid && object.uuid === highlightedObjectUuid) {
            return (
              <div
                key={`highlight-${idx}`}
                className="absolute pointer-events-none transition-opacity duration-150"
                style={{
                  left: object.x,
                  top: object.y,
                  width: object.width,
                  height: object.height,
                  backgroundColor: 'rgba(59, 130, 246, 0.3)',
                  zIndex: 50,
                }}
              />
            );
          }
          return null;
        })}

        {/* Highlighted bbox for objects not normally rendered (e.g. rxn) */}
        {highlightedObjectUuid && data?.objects && (() => {
          // Check if already highlighted above
          const alreadyRendered = objects.some(o => o.uuid === highlightedObjectUuid);
          if (alreadyRendered) return null;
          // Find in raw data
          const rawObj = data.objects.find(o => o.uuid === highlightedObjectUuid);
          if (!rawObj?.bbox) return null;
          const [x1, y1, x2, y2] = rawObj.bbox;
          return (
            <div
              className="absolute pointer-events-none transition-opacity duration-150"
              style={{
                left: x1 * imageProps.width + imageProps.x,
                top: y1 * imageProps.height + imageProps.y,
                width: (x2 - x1) * imageProps.width,
                height: (y2 - y1) * imageProps.height,
                backgroundColor: 'rgba(59, 130, 246, 0.3)',
                zIndex: 50,
              }}
            />
          );
        })()}

        {/* Text-tab selected block highlight */}
        {highlightedBbox && imageProps.width > 0 && (() => {
          const [x1, y1, x2, y2] = highlightedBbox;
          if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) return null;
          return (
            <>
              <style>{`
                @keyframes bbox-pulse {
                  0%   { background-color: rgba(234,179,8,0.1);  border-color: rgba(234,179,8,0.4); transform: scaleX(1);     opacity: 1; }
                  50%  { background-color: rgba(234,179,8,0.35); border-color: rgba(234,179,8,1);   transform: scaleX(1.015); opacity: 1; }
                  100% { background-color: rgba(234,179,8,0);    border-color: rgba(234,179,8,0);   transform: scaleX(1);     opacity: 0; }
                }
              `}</style>
              <div
                className="absolute pointer-events-none"
                style={{
                  left: x1 * imageProps.width + imageProps.x,
                  top: y1 * imageProps.height + imageProps.y,
                  width: (x2 - x1) * imageProps.width,
                  height: (y2 - y1) * imageProps.height,
                  backgroundColor: 'rgba(234,179,8,0.1)',
                  border: '2px solid rgba(234,179,8,0.4)',
                  animation: 'bbox-pulse 1s ease-in-out 2 forwards',
                  transformOrigin: 'center',
                  zIndex: 50,
                }}
              />
            </>
          );
        })()}

        {/* LLM-identified text blocks: pastel mask, non-interactive, rendered
            behind text_unit/molecule/table boxes. */}
        {objects.map((object, idx) => {
          if (object.cls !== "text_block") return null;
          if (!bboxVisibility.text) return null;
          return (
            <div
              key={`block-${idx}`}
              className="absolute pointer-events-none"
              style={{
                left: `${object.x}px`,
                top: `${object.y}px`,
                width: `${object.width}px`,
                height: `${object.height}px`,
                backgroundColor: "rgba(196, 181, 253, 0.28)",
                borderRadius: "3px",
                zIndex: 5,
              }}
              title={object.blockKind ? `block: ${object.blockKind}` : "block"}
            />
          );
        })}

        {objects.map((object, idx) => {
          if (object.cls === "text_unit" && object.transcription) {
            if (!bboxVisibility.text) return null;
            // Hide text units that are mostly inside a molecule bbox
            if (object.uuid && suppressedTextUnitUuids.has(object.uuid)) return null;
            return (
              <div
                key={idx}
                onMouseEnter={() => onHoverObject?.(object.uuid || null)}
                onMouseLeave={() => onHoverObject?.(null)}
              >
                <TextUnit
                  object={{
                    x: object.x,
                    y: object.y,
                    width: object.width,
                    height: object.height,
                    transcription: object.transcription,
                  }}
                  id={idx}
                  {...(selectedTerm && { selectedTerm })}
                />
              </div>
            );
          }
          if (object.cls === "molecule") {
            if (!bboxVisibility.molecules) return null;
            return (
              <div
                key={idx}
                onMouseEnter={() => onHoverObject?.(object.uuid || null)}
                onMouseLeave={() => onHoverObject?.(null)}
              >
                <Molecule
                  object={{
                    x: object.x,
                    y: object.y,
                    width: object.width,
                    height: object.height,
                    transcription: object.transcription || '',
                  }}
                />
              </div>
            );
          }
          if (object.cls === "table") {
            if (!bboxVisibility.tables) return null;
            return (
              <div
                key={idx}
                className="absolute box-border pointer-events-auto z-10 bg-yellow-400/30 border border-yellow-300"
                style={{
                  left: `${object.x}px`,
                  top: `${object.y}px`,
                  width: `${object.width}px`,
                  height: `${object.height}px`,
                }}
                onMouseEnter={() => onHoverObject?.(object.uuid || null)}
                onMouseLeave={() => onHoverObject?.(null)}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default ImageDisplay;
